/* eslint-disable @typescript-eslint/no-unsafe-function-type */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from 'react';
import iro from '@jaames/iro';
import styled, { css, useTheme } from 'styled-components';

interface HsvColor {
    h?: number;
    s?: number;
    v?: number;
    a?: number;
}
interface RgbColor {
    r: number;
    g: number;
    b: number;
    a?: number;
}
interface HslColor {
    h: number;
    s: number;
    l: number;
    a?: number;
}
interface KelvinColor {
    kelvin: number;
}
interface ColorChanges {
	h: boolean
	s: boolean
	v: boolean
	a?: boolean
}
type IroColorValue = string | HsvColor | RgbColor | HslColor | KelvinColor


interface IroColorPickerInterface {
	on: (action: string, callback: Function) => void;
	off: (action: string, callback: Function) => void;
	color: iro.Color
}

export interface ColorInputProps {
	className?: string
	defaultValue?: IroColorValue
	quantize?: boolean
	onChange?: (color: iro.Color) => void
}

const NumQuantizedHueSegments = 10
const QuantizedHueSegments : string[] = []
for(let i = 0; i < NumQuantizedHueSegments; ++i) {					
	QuantizedHueSegments.push(`hsl(${360 - ((i + 0.5) * (360 / NumQuantizedHueSegments))}deg, 100%, 50%) ${Math.round(i * (360 / NumQuantizedHueSegments))}deg ${Math.round(((i + 1)) * (360 / NumQuantizedHueSegments))}deg`)
}

const NumQuantizedSaturationSegments = 3
const QuantizedSaturationSegments : string[] = []
for(let i = 0; i < NumQuantizedSaturationSegments; ++i) {
	const u = ((NumQuantizedSaturationSegments * 2) - 1)
	const p = i == 0 ? 1 : 2;
	const o = i == 0 ? 0 : 1 + ((i - 1) * p);
	const v = i == 0 ? 1 : 1 - ((i + 0.5) * (1 / NumQuantizedSaturationSegments))
	QuantizedSaturationSegments.push(`rgba(255, 255, 255, ${v}) ${Math.round(o * (100 / u))}% ${Math.round(((o + p)) * (100 / u))}%`)
}

const NumQuantizedValueSegments = 5
const QuantizedValueSegments : string[] = []
for(let i = 0; i < NumQuantizedValueSegments; ++i) {
	const v = (i + 0.5) * (255 / NumQuantizedValueSegments)
	QuantizedValueSegments.push(`rgb(${v}, ${v}, ${v}) ${Math.round(i * (100 / NumQuantizedValueSegments))}% ${Math.round(((i + 1)) * (100 / NumQuantizedValueSegments))}%`)
}

const Container = styled.div<{$quantize: boolean, $lowContrast: boolean}>`
	display: contents;
	&>div {
		display: contents !important;
	}
	
	${({$quantize}) => $quantize && css`
		& .IroSliderGradient {
			background: linear-gradient(to top,  ${QuantizedValueSegments.join(",")}) !important;
		}

		& .IroWheelHue {
			background: conic-gradient(${QuantizedHueSegments.join(",")}) !important;
		}
		
		& .IroWheelSaturation {
			background: radial-gradient(circle closest-side, ${QuantizedSaturationSegments.join(",")}) !important;
		}
	`}
`

const quantizeColor = (color: iro.Color, changes?: ColorChanges) : iro.Color => {
	const quantizedColor = {
		h: changes?.h !== false ? (((Math.floor(((color.hue / 360) * NumQuantizedHueSegments)) + 0.5) / NumQuantizedHueSegments) + 0.0) * 360 : color.hue,
		s: changes?.s !== false ? ((Math.round((color.saturation / 100) * (NumQuantizedSaturationSegments - 1)) + 0.0) / (NumQuantizedSaturationSegments - 1)) * 100 : color.saturation,
		v: changes?.v !== false ? Math.max((Math.round((color.value / 100) * (NumQuantizedValueSegments - 1)) + 0.0) / (NumQuantizedValueSegments - 1), 0.05) * 100 : color.value
	}
	color.set(quantizedColor)
	return color
}

export const IroColorPicker: React.FC<ColorInputProps> = ({
	className,
	defaultValue: value = "#FFFFFF",
	quantize = true,
	onChange = () => {}
}) => {
	const el = useRef<HTMLDivElement>(null);
	const colorPicker = useRef<IroColorPickerInterface | null>(null);
	const theme = useTheme()
	const [lastColor, setLastColor] = useState<iro.Color>(() => {
		const color = new iro.Color(value)
		if(quantize) {
			return quantizeColor(color)
		}
		return color
	})
	const [inputEnded, setInputEnded] = useState(false)

	useEffect(() => {
		if (!el.current) {
			return;
		}
		if (!colorPicker.current) {
			// create a new iro color picker and pass component props to it
			colorPicker.current = new (iro.ColorPicker as any)(el.current, { 
				color: lastColor,
				width: window.matchMedia("(pointer: fine)").matches ? 194 : 164,
				handleRadius: 10,
				handleSvg: '#iro-handle',
				handleProps: { x: 10, y: 10},
				padding: 4,	
				margin: 10,			
				layout: [
					{ 
						component: iro.ui.Slider,
						options: {
						  sliderType: 'value',
							borderColor: '#ffffff'
						}
					  },
					{ 
						component: iro.ui.Wheel,
						options: {
							wheelLightness: true,
							borderColor: '#ffffff'
						}
					},
				],
				layoutDirection: "horizontal"
			});

			el.current.querySelector(".IroSlider .IroHandle use")?.setAttribute("stroke", "#fff")
			el.current.querySelector(".IroWheel .IroHandle use")?.setAttribute("stroke", lastColor.hsl.l > 90 ? theme.colours.background.normal : "#fff")
		}
	}, [lastColor, theme.colours.background.normal])

	useEffect(() => {
		// call onColorChange prop whenever the color changes
		const picker = colorPicker.current
		if (!picker) {
			return;
		}

		const inputChange = (color: iro.Color, changes: ColorChanges) => {
			if(!quantize) {
				onChange(color);
				setLastColor(color)
			}
			else if(picker) {
				color = quantizeColor(color, changes)
				setLastColor(color)
				picker.color.set(color);
			}
			if(el.current) {
				el.current.querySelector(".IroWheel .IroHandle use")?.setAttribute("stroke", color.hsl.l > 90 ? theme.colours.background.normal : "#fff")
			}
		}

		const inputEnd = () => {
			setInputEnded(true)
		}

		picker.on("input:change", inputChange)
		picker.on("input:end", inputEnd)

		return () => {
			picker.off("input:change", inputChange)
			picker.off("input:end", inputEnd)
		}
	}, [onChange, quantize, theme.colours.background.normal])

	useEffect(() => {
		if(inputEnded) {
			if(quantize) {
				onChange(lastColor);
			}
			setInputEnded(false)
		}
	}, [inputEnded, lastColor, onChange, quantize])

	return (
		<>
			<Container ref={el} className={className} $quantize={quantize} $lowContrast={lastColor.hsl.l > 90}/>
			<svg style={{display: 'none'}}>
				<defs>
					<g id="iro-handle">
						<circle cx="0" cy="0" r="9" fill="transparent" strokeWidth="3"/>
					</g>
				</defs>
			</svg>
		</>
	)
};
